import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { HotelClinicCreateTaskPassengerPayload } from "@/pages/HotelPlanning/clinic-arrival/utils/hotelClinicCreateTaskPayload";
import {
    useAssignHotelClinicTaskPassengersMutation,
    useUnassignHotelClinicTaskPassengersMutation,
} from "@/requests/hotel-clinic.mutations";
import { useHotelClinicArrivalDate } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

export type ClinicArrivalTaskDetailAssignationUnassignParams = {
    taskId: string;
    listItemIds: string[];
    /** When true, skip success snackbar (e.g. DnD / chained moves). */
    silent?: boolean;
};

export type ClinicArrivalTaskDetailAssignationAssignParams = {
    taskId: string;
    passengers: HotelClinicCreateTaskPassengerPayload[];
    /** When true, skip success snackbar (e.g. DnD / chained moves). */
    silent?: boolean;
};

function useClinicArrivalTaskDetailAssignation({ invalidateQuery = true }: { invalidateQuery?: boolean } = {}) {
    const date = useHotelClinicArrivalDate();
    const isoDate = date.toISODate() ?? "";
    const { mutateAsync: unassignMutate, isPending: unassignPending } = useUnassignHotelClinicTaskPassengersMutation({
        isoDate,
        invalidateQuery,
    });
    const { mutateAsync: assignMutate, isPending: assignPending } = useAssignHotelClinicTaskPassengersMutation();
    const { t } = useTranslation();

    const handleUnassignPassengersFromTask = useCallback(
        async ({ taskId, listItemIds, silent }: ClinicArrivalTaskDetailAssignationUnassignParams) => {
            if (unassignPending || !listItemIds.length || !taskId) return;

            await unassignMutate({ taskId, passengers: listItemIds });

            if (!silent) {
                enqueueSnackbar(t("responses.bulkTaskUnassign", { count: listItemIds.length || 1 }), {
                    variant: "success",
                });
            }
        },
        [unassignMutate, unassignPending, t]
    );

    const handleAssignPassengersToTask = useCallback(
        async ({ taskId, passengers, silent }: ClinicArrivalTaskDetailAssignationAssignParams) => {
            if (assignPending || !passengers.length || !taskId) return;

            await assignMutate({ taskId, passengers });

            if (!silent) {
                enqueueSnackbar(t("responses.bulkTaskAssign", { count: passengers.length || 1 }), {
                    variant: "success",
                });
            }
        },
        [assignMutate, assignPending, t]
    );

    const assignationPending = unassignPending || assignPending;

    return {
        handleUnassignPassengersFromTask,
        handleAssignPassengersToTask,
        unassignPending,
        assignPending,
        assignationPending,
    };
}

export default useClinicArrivalTaskDetailAssignation;
